import React, { useState, useEffect } from 'react';
import Breadcrumbs from '../components/BreadCrumbs';
import StyledHeadingText from '../components/Fonts/StyledHeadingText';
import TailwindStatsCard from '../components/cards/TailwindStatsCard';
import { CustomCard } from '../components/cards/CustomCard';
import { getSalespersonMonthlySalesStats } from '../services/staff_dashboard';
import CustomLoading from '../components/customLoading/CustomLoading';
import { toast } from 'react-toastify';

function StaffDashboard() {
    const [loading, setLoading] = useState(false);
    const [isMonthlyStats, setIsMonthlyStats] = useState(false);
    // State for dashboard data
    const [dashboardData, setDashboardData] = useState({
        assigned_leads_today: 0,
        calls_completed_today: 0,
        orders_placed_today: 0,
        // conversion_rate: 0,
        total_revenue_today: 0,
        follow_ups_pending: 0,

        // Monthly / overall staff stats (optional keys from backend)
        month: "",
        date: null,
        total_sales: 0,
        total_returned_amount: null,
        total_cancelled_amount: null,
        total_returned_and_cancelled_amount: null,
        actual_sales: null,
        total_target: null,
        percentage_completed: 0,
        orders_count: 0,
        average_order_value: 0,
        avg_order_value_day_wise: 0,
        days_passed: 0,
        target_remaining_amount: null,
    });

    // Icons for stats cards
    const PhoneIcon = () => (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
    );

    const OrderIcon = () => (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M8 11v6a2 2 0 002 2h4a2 2 0 002-2v-6M8 11h8" />
        </svg>
    );

    const TargetIcon = () => (
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
    );

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch monthly sales stats (dashboard/salesperson-monthly-sales-stats)
            try {
                let monthlyRes = await getSalespersonMonthlySalesStats();
                if (monthlyRes.status === 200 && monthlyRes.data) {
                    const monthlyPayload = monthlyRes.data?.data ?? monthlyRes.data;
                    setDashboardData(prev => ({
                        ...prev,
                        ...monthlyPayload,
                    }));

                    setIsMonthlyStats(Boolean(
                        monthlyPayload?.total_sales !== undefined ||
                        monthlyPayload?.month !== undefined
                    ));
                } else {
                    setIsMonthlyStats(false);
                }
            } catch (err) {
                // If monthly endpoint fails, keep showing today's dashboard.
                setIsMonthlyStats(false);
            }
        } catch (error) {
            console.error("Error:", error);
            toast.error("Error fetching dashboard data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const formatINR = (value) => {
        const n = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(n)) return "--";
        const abs = Math.abs(n);
        if (abs >= 1000) {
            const compact = (n / 1000).toLocaleString("en-IN", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
            return `₹${compact}K`;
        }
        return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDateLabel = (ymd) => {
        if (ymd == null || ymd === "") return null;
        const d = new Date(`${String(ymd).slice(0, 10)}T12:00:00`);
        if (Number.isNaN(d.getTime())) return String(ymd);
        return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
    };

    const completionPctRaw = Number(dashboardData?.percentage_completed ?? 0);
    const completionPct = Number.isFinite(completionPctRaw) ? Math.max(0, Math.min(100, completionPctRaw)) : 0;

    const Pill = ({ children, tone = "slate" }) => {
        const toneClass = {
            slate: "bg-slate-50 text-slate-700 border-slate-200",
            indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
            emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
            amber: "bg-amber-50 text-amber-800 border-amber-200",
            rose: "bg-rose-50 text-rose-700 border-rose-200",
        }[tone] || "bg-slate-50 text-slate-700 border-slate-200";
        return (
            <span className={`inline-flex items-center border px-2 py-0.5 rounded-full text-[11px] font-semibold ${toneClass}`}>
                {children}
            </span>
        );
    };

    return (
        <>
            {loading && <CustomLoading />}
            <Breadcrumbs items={[
                { name: 'Home', link: '/dashboard' },
                { name: 'Staff Dashboard' },
            ]} />

            <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                <div>
                    <StyledHeadingText
                        text={isMonthlyStats ? "Monthly sales" : "Telecalling"}
                        size={"text-2xl"}
                    />
                    {isMonthlyStats ? (
                        <div className="text-xs text-gray-500 mt-0.5">Sales &amp; target</div>
                    ) : null}
                </div>
                {isMonthlyStats && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {dashboardData?.month ? <Pill tone="indigo">{dashboardData.month}</Pill> : null}
                        {formatDateLabel(dashboardData?.date) ? (
                            <Pill tone="slate">{formatDateLabel(dashboardData.date)}</Pill>
                        ) : null}
                        <Pill tone={completionPct >= 80 ? "emerald" : completionPct >= 50 ? "amber" : "rose"}>
                            {completionPct}%
                        </Pill>
                    </div>
                )}
            </div>

            {/* Sales Monthly Stats */}
            {isMonthlyStats ? (
                <div className="mb-4 rounded-lg border border-gray-200 bg-gradient-to-br from-slate-50/90 via-white to-emerald-50/40 p-3 shadow-sm">
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                        <div className="rounded-md border border-emerald-100 bg-white/90 px-2 py-1.5">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">Sales</div>
                            <div className="text-base font-bold text-emerald-700">{formatINR(dashboardData.total_sales)}</div>
                        </div>
                        <div className="rounded-md border border-indigo-100 bg-white/90 px-2 py-1.5">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">Target</div>
                            <div className="text-base font-bold text-indigo-700">
                                {dashboardData.total_target != null ? formatINR(dashboardData.total_target) : "—"}
                            </div>
                        </div>
                        <div className="rounded-md border border-slate-200 bg-white/90 px-2 py-1.5">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">Orders</div>
                            <div className="text-base font-bold text-gray-900">{dashboardData.orders_count ?? "—"}</div>
                        </div>
                        <div className="rounded-md border border-cyan-100 bg-white/90 px-2 py-1.5">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">Actual</div>
                            <div className="text-base font-bold text-cyan-800">
                                {dashboardData.actual_sales != null ? formatINR(dashboardData.actual_sales) : "—"}
                            </div>
                        </div>
                        <div className="rounded-md border border-slate-200 bg-white/90 px-2 py-1.5">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">Ret. + can.</div>
                            <div className="text-base font-bold text-gray-900">
                                {dashboardData.total_returned_and_cancelled_amount != null
                                    ? formatINR(dashboardData.total_returned_and_cancelled_amount)
                                    : "—"}
                            </div>
                        </div>
                        <div className="rounded-md border border-rose-100 bg-white/90 px-2 py-1.5 col-span-2 sm:col-span-1 lg:col-span-1">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">Left / %</div>
                            <div className="text-base font-bold text-rose-700">
                                {dashboardData.target_remaining_amount != null ? formatINR(dashboardData.target_remaining_amount) : "—"}
                                <span className="text-gray-500 font-semibold text-sm"> · {completionPct}%</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-[11px] text-gray-600">
                        <span>Days passed: <strong className="text-gray-900">{dashboardData.days_passed ?? "—"}</strong></span>
                    </div>

                    <div className="mt-2">
                        <div className="h-1.5 w-full overflow-hidden rounded-full border border-gray-100 bg-white/80">
                            <div
                                className="h-1.5 rounded-full bg-gradient-to-r from-indigo-500 to-emerald-500"
                                style={{ width: `${completionPct}%` }}
                            />
                        </div>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2">
                        <div className="rounded-md border border-gray-100 bg-white/80 px-2 py-1.5">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">AOV</div>
                            <div className="text-sm font-bold text-gray-900">{formatINR(dashboardData.average_order_value)}</div>
                        </div>
                        <div className="rounded-md border border-gray-100 bg-white/80 px-2 py-1.5">
                            <div className="text-[10px] font-semibold text-gray-500 uppercase">AOV / day</div>
                            <div className="text-sm font-bold text-gray-900">{formatINR(dashboardData.avg_order_value_day_wise)}</div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3 mb-4 md:grid-cols-2 lg:grid-cols-4">
                    <TailwindStatsCard
                        title="Assigned Leads"
                        value={dashboardData.assigned_leads_today}
                        icon={<TargetIcon />}
                        description="Total leads for today"
                    />

                    <TailwindStatsCard
                        title="Calls Completed"
                        value={dashboardData.calls_completed_today}
                        icon={<PhoneIcon />}
                        description={`${dashboardData.assigned_leads_today ? Math.round((dashboardData.calls_completed_today / dashboardData.assigned_leads_today) * 100) : 0}% of assigned leads`}
                    />

                    <TailwindStatsCard
                        title="Orders Placed"
                        value={dashboardData.orders_placed_today}
                        icon={<OrderIcon />}
                        description="Successful conversions today"
                    />

                    {/* Today's Revenue */}
                    <div className="lg:col-span-1">
                        <CustomCard title="Today's Revenue">
                            <div className="text-center py-2">
                                <div className="text-2xl font-bold text-green-600">
                                    {dashboardData.total_revenue_today}
                                </div>
                            </div>
                        </CustomCard>
                    </div>
                </div>
            )}

            {/* Main Content Grid */}
            {!isMonthlyStats && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                    <div className="lg:col-span-1">
                        <CustomCard title="Follow-ups">
                            <div className="text-center py-2">
                                <div className="text-2xl font-bold text-primary">
                                    {dashboardData.follow_ups_pending}
                                </div>
                            </div>
                        </CustomCard>
                    </div>
                </div>
            )}
        </>
    );
}

export default StaffDashboard;
